import React, { CSSProperties, Fragment, useEffect, useState } from "react";
import { TableRow } from "@material-ui/core";
import {
  Table,
  TableContainer,
  TableHead,
  TableCell,
  TableBody,
  useTheme,
  Theme,
  Typography,
  Stack,
  Skeleton,
} from "@mui/material";
import PaginationCustom from "@components/Pagination";
import { Drivers } from "../models";
import { LinkModal } from "./LinkModal";
import { useMyDriversContext } from "@context/MyDriversContext";
import Button from "@components/Button";
import { format } from "date-fns";
import { headers } from "../constants";

const ROWS_PER_PAGE = 10;

const MyDriversTable: React.FC = () => {
  const [showLink, setShowLink] = useState<boolean>(false);
  const [linkData, setLinkData] = useState<{
    id: string;
    name: string;
    companies: Drivers["companies"];
  }>(
    {} as {
      id: string;
      name: string;
      companies: Drivers["companies"];
    }
  );

  const {
    data,
    page,
    loading,
    setListCompany,
    setPage,
    fetchAllDrivers,
  } = useMyDriversContext();

  const theme = useTheme();
  const styles = useStyles(theme);

  const handleOnChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleOnChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const Rows = React.memo(({ data }: { data: Drivers }) => {
    const dataWithFormattedDate = data.companies.map((items) => ({
      ...items,
      lastSchedule:
        items.lastSchedule !== null
          ? format(
              new Date(items.lastSchedule as string),
              "dd/MM/yyyy 'às' HH:mm"
            )
          : "-",
    }));

    const handleLinkData = () => {
      setLinkData((prevState) => ({
        ...prevState,
        id: data.id,
        name: data.name,
        companies: dataWithFormattedDate,
      }));

      setShowLink(true);

      setListCompany((prevState) => ({
        ...prevState,
        companyDocuments: data.companies.map((item) => item.CNPJ),
      }));
    };

    const handleTranslateStatus = () => {
      switch (data.status) {
        case "INACTIVE":
          return "Inativo";
        case "ACTIVE":
          return "Ativo";
        default:
          return "Desconhecido";
      }
    };
    const items = [data.name, data.email, handleTranslateStatus()];

    return (
      <TableRow key={data.id}>
        {items.map((item, index) => (
          <TableCell
            key={`${item}+${index + 1}`}
            sx={
              !index
                ? { ...styles.table_first_column, ...styles.table_cells }
                : styles.table_cells
            }
          >
            {loading ? <Skeleton animation="wave" height="5rem" /> : item}
          </TableCell>
        ))}
        <TableCell align="center" sx={{ border: "none" }}>
          {loading ? (
            <Skeleton animation="wave" height="5rem" />
          ) : (
            <Stack sx={styles.linked_companies}>
              {data.companies.slice(0, 3).map((item) => (
                <Typography
                  key={`${item.companyName}-${item.CNPJ}`}
                  sx={styles.tag_company}
                >
                  {item.companyName}
                </Typography>
              ))}

              {data.companies.length > 3 && (
                <Typography sx={styles.more_companies}>
                  +{data.companies.length - 3}
                </Typography>
              )}
            </Stack>
          )}
        </TableCell>

        <TableCell align="right" sx={{ border: "none" }}>
          {loading ? (
            <Skeleton animation="wave" height="5rem" />
          ) : (
            <Button onClick={() => handleLinkData()} sx={{ fontWeight: 600 }}>
              Editar
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  });

  useEffect(() => {
    fetchAllDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <Fragment>
      {showLink && (
        <LinkModal
          open={showLink}
          onClose={() => setShowLink(false)}
          data={linkData}
        />
      )}

      <TableContainer sx={{ overflowX: "scroll", marginBottom: "2rem" }}>
        <Table
          sx={{
            borderCollapse: "separate",
            borderSpacing: "0 2rem",
          }}
        >
          <TableHead sx={{ backgroundColor: theme.palette.background.paper }}>
            {headers.map((item) => (
              <TableCell
                align={item.align}
                key={item.label}
                sx={
                  item.label === "Nome do motorista"
                    ? { ...styles.table_first_column, ...styles.table_header }
                    : styles.table_header
                }
              >
                {loading ? (
                  <Skeleton animation="wave" height="5rem" />
                ) : (
                  item.label
                )}
              </TableCell>
            ))}
          </TableHead>

          <TableBody
            sx={{
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {data.rows.map((item) => (
              <Rows key={String(item.id)} data={item} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && (
        <PaginationCustom
          onPageChange={handleOnChangePage}
          onChangeHandle={handleOnChange}
          page={page}
          rowsPerPage={ROWS_PER_PAGE}
          count={data.count ?? 0}
        />
      )}
    </Fragment>
  );
};

const useStyles = (theme: Theme): Record<string, CSSProperties> => ({
  table_first_column: {
    position: "sticky",
    left: 0,
    backgroundColor: theme.palette.background.paper,
  },
  table_header: {
    fontSize: "1.5rem",
    fontWeight: 600,
    border: "none",
  },
  table_cells: {
    fontSize: "1.5rem",
    border: "none",
  },
  linked_companies: {
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "1rem",
    alignItems: "center",
  },
  tag_company: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: "8px",
    paddingLeft: ".4rem",
    paddingRight: ".4rem",
    textAlign: "center",
  },
  menu_items: {
    flexDirection: "row",
    gap: "1rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    alignItems: "center",
  },
  more_companies: {
    backgroundColor: "#FFF5F7",
    color: theme.palette.error.main,
    fontWeight: 500,
    fontSize: "1.2rem",
    borderRadius: "50%",
    textAlign: "center",
    padding: ".2rem",
  },
});

export { MyDriversTable };
